import {
  Fragment,
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useForm, Controller, Resolver, useWatch } from 'react-hook-form'
import { useSelector } from 'react-redux'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid2'
import { useMediaQuery } from '@mui/material'
import { yupResolver } from '@hookform/resolvers/yup'
import { RootState } from 'store'
import { Modal } from 'components'
import {
  createValidationSchema,
  getAttributeWarnings,
  createNestedValidationSchema,
  getNestedAttributeWarnings,
} from 'utils'
import { TAttribute, TProduct } from 'types/models'
import { ParameterTitle } from 'components/ui/ParameterRange/PrameterTitle'
import { CustomTextField } from './dialogs.styled'
import * as sxStyles from './dialogs.styled'
import { isEqual } from 'lodash'
import { CrudAction, Entity, usePermissions } from '../../../../../utils/hooks'
import { FormEditableContext } from '../../../../../providers'
import { ParameterRange } from '../../../../../components/ui/ParameterRange/ParameterRange'

interface IEditModalProps {
  onConfirm(data: any): void
  initialData?: Partial<TProduct>
  mode?: 'add' | 'edit'
  validationRules?: Record<string, any>
  categoriesWithAttributes?: any[]
  titleAdd?: string
  titleEdit?: string
  namePlaceholderAdd?: string
}

const useEditModal = ({
  onConfirm,
  initialData,
  mode = 'add',
  validationRules: validationRulesFromProps,
  categoriesWithAttributes,
  titleAdd,
  titleEdit,
  namePlaceholderAdd,
}: IEditModalProps): [ReactElement, VoidFunction, VoidFunction] => {
  const { checkPermission } = usePermissions()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [attributeWarnings, setAttributeWarnings] = useState<
    Record<string, { range_min?: string; range_max?: string }>
  >({})

  const validationRules = useSelector(
    (state: RootState) => state.validations.validationRules
  )

  const effectiveValidationRules = validationRulesFromProps ?? validationRules

  const validationSchema = useMemo(() => {
    if (categoriesWithAttributes && categoriesWithAttributes.length > 0) {
      return createNestedValidationSchema(
        categoriesWithAttributes,
        effectiveValidationRules
      )
    }
    return createValidationSchema(effectiveValidationRules)
  }, [effectiveValidationRules, categoriesWithAttributes])

  const companyId = useSelector(
    (state: RootState) => state.user.profile?.company?.id
  )

  const smallScreen = useMediaQuery('(max-width:1500px)')

  const handleCloseModal = () => {
    toggleModal()
    reset()
  }
  const toggleModal = () => setIsOpen(!isOpen)

  const defaultValues = useMemo(() => {
    if (
      categoriesWithAttributes &&
      categoriesWithAttributes.length > 0 &&
      initialData?.attributes
    ) {
      const attributes: any = {}
      categoriesWithAttributes.forEach((category) => {
        attributes[category.id] = {}
        category.attributes.forEach((attr) => {
          const matchingAttr = initialData?.attributes?.find(
            (a) => a.name === attr.name && a.category_id === category.id
          )

          attributes[category.id][attr.id] = {
            min: Number(matchingAttr?.range_min) || 0,
            max: Number(matchingAttr?.range_max) || 0,
          }
        })
      })

      return {
        id: initialData?.id,
        name: initialData?.name || '',
        company_id: initialData?.company_id || companyId,
        attributes,
      }
    }

    return {
      name: initialData?.name || '',
      attributes: initialData?.attributes || [],
      company_id: initialData?.company_id || companyId,
    }
  }, [initialData, companyId, categoriesWithAttributes])

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(validationSchema as any) as Resolver<any, any>,
  })

  const watchedAttributes = useWatch({ control, name: 'attributes' })

  useEffect(() => {
    let warnings = {}
    if (categoriesWithAttributes && categoriesWithAttributes.length > 0) {
      // Use nested attribute warnings for categorized attributes (Flour case)
      warnings = getNestedAttributeWarnings(
        watchedAttributes,
        categoriesWithAttributes,
        effectiveValidationRules
      )
    } else {
      // Use flat attribute warnings for non-categorized attributes (Wheat case)
      warnings = getAttributeWarnings(
        watchedAttributes,
        effectiveValidationRules
      )
    }
    setAttributeWarnings(warnings)
  }, [watchedAttributes, effectiveValidationRules, categoriesWithAttributes])

  const prevInitialDataRef = useRef(initialData)
  useEffect(() => {
    if (
      initialData &&
      (!prevInitialDataRef.current ||
        prevInitialDataRef.current.name !== initialData.name ||
        !isEqual(prevInitialDataRef.current.attributes, initialData.attributes))
    ) {
      if (categoriesWithAttributes && categoriesWithAttributes.length > 0) {
        reset(defaultValues)
      } else {
        reset(initialData)
      }

      prevInitialDataRef.current = {
        ...initialData,
        attributes: initialData.attributes ? [...initialData.attributes] : [],
      }
    }
  }, [initialData, reset, categoriesWithAttributes, defaultValues])

  const normalizeAttributes = (attributes: TAttribute[]): TAttribute[] => {
    return attributes.map((attr) => ({
      ...attr,
      range_min: attr.range_min ?? 0,
      range_max: attr.range_max ?? 0,
      range_avg: attr.range_avg ?? 0,
    }))
  }

  const normalizeProduct = (product: any): any => {
    if (
      categoriesWithAttributes &&
      categoriesWithAttributes.length > 0 &&
      typeof product.attributes === 'object' &&
      !Array.isArray(product.attributes)
    ) {
      const attributes: any[] = []

      Object.entries(product.attributes).forEach(
        ([categoryIdStr, attrMap]: [string, any]) => {
          Object.entries(attrMap).forEach(
            ([attrIdStr, values]: [string, any]) => {
              const categoryId = Number(categoryIdStr)
              const attrId = Number(attrIdStr)

              const attr = categoriesWithAttributes
                .find((cat) => cat.id === categoryId)
                ?.attributes.find((a) => a.id === attrId)

              if (attr) {
                attributes.push({
                  name: attr.name,
                  category_id: categoryId,
                  range_min: parseFloat(values.min),
                  range_max: parseFloat(values.max),
                })
              }
            }
          )
        }
      )

      return {
        ...product,
        attributes,
      }
    }

    return {
      ...product,
      attributes: normalizeAttributes(product.attributes),
    }
  }

  const onSubmit = (data: any) => {
    const normalizedProduct = normalizeProduct(data)
    onConfirm(normalizedProduct)
  }

  const isReadOnly =
    mode !== 'add'
      ? !checkPermission(Entity.PRODUCT, CrudAction.UPDATE)
      : !checkPermission(Entity.PRODUCT, CrudAction.CREATE)

  return [
    <FormEditableContext.Provider
      value={{
        isEditable: !isReadOnly,
      }}
    >
      <Modal
        isOpen={isOpen}
        onClose={handleCloseModal}
        title={
          mode === 'add'
            ? (titleAdd ?? 'Add Blending Spec')
            : (titleEdit ?? 'Edit Blending Spec')
        }
        onConfirm={handleSubmit(onSubmit)}
        confirmButtonDisabled={isReadOnly}
        onConfirmButtonText="Save"
      >
        <Box px="30px" py="20px" sx={sxStyles.mainBox}>
          <Grid container spacing={2}>
            <Grid size={smallScreen ? 12 : 5}>
              <Controller
                name="name"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <CustomTextField
                    {...field}
                    label={
                      mode === 'edit'
                        ? initialData?.name || ''
                        : (namePlaceholderAdd ?? 'Blending Spec Name')
                    }
                    variant="outlined"
                    sx={
                      smallScreen
                        ? sxStyles.smallScreenNameTextField
                        : sxStyles.regularScreenNameTextField
                    }
                    error={!!error}
                    helperText={error ? error.message : ''}
                  />
                )}
              />
            </Grid>
            {categoriesWithAttributes && categoriesWithAttributes.length > 0 ? (
              <Grid container spacing={2} sx={sxStyles.mainGrid}>
                <Controller
                  name="attributes"
                  control={control}
                  rules={{ required: 'Attributes are required' }}
                  render={() => (
                    <Fragment>
                      {categoriesWithAttributes.map((category) => (
                        <Grid
                          size={smallScreen ? 12 : 6}
                          key={category.id}
                          sx={sxStyles.parametersBox}
                        >
                          <Box>
                            <ParameterTitle parametersTitle={category.name} />
                            {category.attributes.map((attr) => (
                              <Box key={attr.id}>
                                <ParameterRange
                                  key={attr.id}
                                  attribute={attr}
                                  control={control}
                                  attributeIndex={attr.id}
                                  nameMin={`attributes.${category.id}.${attr.id}.min`}
                                  nameMax={`attributes.${category.id}.${attr.id}.max`}
                                  warningMinMessage={
                                    attributeWarnings[attr.name]?.range_min
                                  }
                                  warningMaxMessage={
                                    attributeWarnings[attr.name]?.range_max
                                  }
                                  validationRules={effectiveValidationRules}
                                />
                                <Box sx={sxStyles.brBox}>
                                  <br />
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </Grid>
                      ))}
                    </Fragment>
                  )}
                />
              </Grid>
            ) : (
              <Grid size={12}>
                <Box sx={sxStyles.parametersBox}>
                  <ParameterTitle parametersTitle={'Parameter'} />
                  <Box sx={sxStyles.brBox}>
                    <br />
                  </Box>
                  <Controller
                    name="attributes"
                    control={control}
                    rules={{ required: 'Attributes are required' }}
                    render={({ field }) => (
                      <Fragment>
                        {field.value?.map((attribute, index) => {
                          return (
                            <Box key={attribute.name}>
                              <ParameterRange
                                key={attribute.name}
                                attribute={attribute as TAttribute}
                                control={control}
                                attributeIndex={index}
                                warningMinMessage={
                                  attributeWarnings[attribute.name]?.range_min
                                }
                                warningMaxMessage={
                                  attributeWarnings[attribute.name]?.range_max
                                }
                                validationRules={effectiveValidationRules}
                              />
                              <Box sx={sxStyles.brBox}>
                                <br />
                              </Box>
                            </Box>
                          )
                        })}
                      </Fragment>
                    )}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
          {smallScreen ? <></> : <br />}
          <hr />
        </Box>
      </Modal>
    </FormEditableContext.Provider>,
    toggleModal,
    reset,
  ]
}

export default useEditModal
